import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { publicBlogPost } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Clock, Eye, Calendar, ChevronRight, Newspaper } from 'lucide-react';

const SITE_LOGO = 'https://customer-assets.emergentagent.com/job_87cd0f70-4f18-4348-a2fd-e2666a61734d/artifacts/ozrawt0t_IMG_0439.jpeg';
const SITE_BASE = 'https://alveetrade.com';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
}

function upsertMeta(attr, key, value) {
  if (!value) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}
function upsertLink(rel, href) {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setNotFound(false);
    publicBlogPost(slug).then((d) => {
      if (cancel) return;
      setPost(d.post);
      setRelated(d.related || []);
    }).catch((e) => {
      if (cancel) return;
      if (e?.response?.status === 404) setNotFound(true);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [slug]);

  // Lightweight SEO meta injection — set <title>, description, og: tags
  useEffect(() => {
    if (!post) return;
    const title = post.seo_title || post.title || 'AlveeTrade Blog';
    const description = post.seo_description || post.summary || '';
    const url = `${SITE_BASE}/blog/${slug}`;
    document.title = `${title} · AlveeTrade Blog`;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:type', 'article');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    if (post.cover_image) upsertMeta('property', 'og:image', post.cover_image);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    if (post.cover_image) upsertMeta('name', 'twitter:image', post.cover_image);
    upsertLink('canonical', url);
  }, [post, slug]);

  const title = post?.seo_title || post?.title || 'AlveeTrade Blog';
  const description = post?.seo_description || post?.summary || '';
  const url = `${SITE_BASE}/blog/${slug}`;

  return (
    <div className="min-h-screen bg-[hsl(240_6%_6%)] text-white" data-testid="blog-post-page">
      {/* SEO meta is injected via useEffect helpers above */}

      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[hsl(240_6%_6%)/0.85] border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-2 group">
            <img src={SITE_LOGO} alt="AlveeTrade" className="h-8 w-8 rounded-sm" />
            <div className="leading-tight">
              <div className="font-display tracking-wider text-sm">ALVEETRADE</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)]">Blog</div>
            </div>
          </Link>
          <Button variant="outline" size="sm" onClick={() => navigate('/blog')}
            className="border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white gap-2"
            data-testid="blog-back-to-list">
            <ArrowLeft className="h-3.5 w-3.5" /> All posts
          </Button>
        </div>
      </header>

      {loading && (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center text-muted-foreground text-sm">Loading article…</div>
      )}

      {notFound && !loading && (
        <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="blog-not-found">
          <h1 className="text-2xl font-bold">Article not found</h1>
          <p className="text-muted-foreground mt-2 text-sm">It may have been moved or unpublished.</p>
          <Button onClick={() => navigate('/blog')} className="mt-6 bg-[hsl(43_92%_56%)] hover:bg-[hsl(43_92%_50%)] text-black font-semibold">
            Browse all posts
          </Button>
        </div>
      )}

      {post && !loading && (
        <article className="max-w-3xl mx-auto px-4 pt-6 pb-16">
          {/* Breadcrumb */}
          <nav className="text-[11px] text-muted-foreground mb-4 flex items-center gap-1 flex-wrap">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/blog" className="hover:text-white">Blog</Link>
            {post.category && (
              <>
                <ChevronRight className="h-3 w-3" />
                <Link to={`/blog?category=${encodeURIComponent(post.category)}`} className="hover:text-white">
                  {post.category}
                </Link>
              </>
            )}
          </nav>

          {/* Title block */}
          <div className="mb-6">
            {post.category && (
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(43_92%_56%)] font-semibold mb-2">
                {post.category}
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{post.title}</h1>
            {post.summary && (
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">{post.summary}</p>
            )}
            <div className="mt-5 flex items-center gap-4 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {fmtDate(post.published_at)}</span>
              {post.reading_minutes && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {post.reading_minutes} min read</span>}
              <span className="inline-flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" /> {post.views} views</span>
            </div>
          </div>

          {/* Cover */}
          {post.cover_image && (
            <img src={post.cover_image} alt={post.title}
              className="w-full aspect-[16/9] object-cover rounded-xl border border-white/10 mb-8" />
          )}

          {/* Body */}
          <div className="blog-prose"
            dangerouslySetInnerHTML={{ __html: post.content_html }}
            data-testid="blog-post-body" />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mr-1">Tags</span>
              {post.tags.map((t) => (
                <span key={t} className="text-[11px] px-2 py-1 rounded-full border border-white/10 bg-white/[0.02] text-muted-foreground">
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-12">
              <h2 className="text-lg font-bold mb-4">Related articles</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link to={`/blog/${r.slug}`} key={r.slug} className="group" data-testid={`blog-related-${r.slug}`}>
                    <Card className="card-surface overflow-hidden h-full">
                      <div className="aspect-[16/9] bg-white/[0.04]">
                        {r.cover_image
                          ? <img src={r.cover_image} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Newspaper className="h-8 w-8 opacity-30" /></div>
                        }
                      </div>
                      <CardContent className="p-3 space-y-1">
                        {r.category && <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(43_92%_56%)] font-semibold">{r.category}</div>}
                        <h3 className="font-semibold text-sm leading-snug group-hover:text-[hsl(43_92%_60%)] transition-colors">{r.title}</h3>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* CTA back */}
          <div className="mt-12 text-center">
            <Link to="/blog">
              <Button variant="outline" className="border-white/10 bg-white/[0.02] gap-2">
                <ArrowLeft className="h-4 w-4" /> Back to all articles
              </Button>
            </Link>
          </div>
        </article>
      )}

      <footer className="border-t border-white/5 mt-10">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center justify-between flex-wrap gap-3 text-xs text-muted-foreground">
          <div>© {new Date().getFullYear()} AlveeTrade.</div>
          <Link to="/" className="hover:text-white">alveetrade.com →</Link>
        </div>
      </footer>
    </div>
  );
}
